import { useState, useEffect, useCallback } from 'react'
import { M365Device, M365UserDevicesResponse } from '../../types/m365Users'
import { useAuth } from '../../hooks/useAuth'
import { api } from '../../services/api'

interface DevicesSectionProps {
  userId: string
  customerId: string
  customerTenantId: string
}

export function DevicesSection({
  userId,
  customerId,
  customerTenantId,
}: DevicesSectionProps) {
  const { getCustomerGraphAccessToken } = useAuth()
  const [ownedDevices, setOwnedDevices] = useState<M365Device[]>([])
  const [registeredDevices, setRegisteredDevices] = useState<M365Device[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchDevices = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)

      const token = await getCustomerGraphAccessToken(customerTenantId)
      const response = await api.getM365UserDevices(token, customerId, userId) as M365UserDevicesResponse

      setOwnedDevices(response.ownedDevices || [])
      setRegisteredDevices(response.registeredDevices || [])
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load devices'
      if (errorMessage.includes('Insufficient privileges') || errorMessage.includes('Authorization_RequestDenied')) {
        setError('Insufficient permissions to view devices. Device.Read.All permission may be required.')
      } else {
        setError(errorMessage)
      }
    } finally {
      setLoading(false)
    }
  }, [userId, customerId, customerTenantId, getCustomerGraphAccessToken])

  useEffect(() => {
    fetchDevices()
  }, [fetchDevices])

  const getOSIcon = (os?: string) => {
    const osLower = (os || '').toLowerCase()
    if (osLower.includes('windows')) {
      return (
        <svg className="h-5 w-5 text-blue-500" viewBox="0 0 24 24" fill="currentColor">
          <path d="M0 3.449L9.75 2.1v9.451H0m10.949-9.602L24 0v11.4H10.949M0 12.6h9.75v9.451L0 20.699M10.949 12.6H24V24l-12.9-1.801" />
        </svg>
      )
    }
    if (osLower.includes('ios') || osLower.includes('iphone') || osLower.includes('ipad')) {
      return (
        <svg className="h-5 w-5 text-gray-700" viewBox="0 0 24 24" fill="currentColor">
          <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
        </svg>
      )
    }
    if (osLower.includes('android')) {
      return (
        <svg className="h-5 w-5 text-green-500" viewBox="0 0 24 24" fill="currentColor">
          <path d="M17.6 9.48l1.84-3.18c.16-.31.04-.69-.26-.85-.29-.15-.65-.06-.83.22l-1.88 3.24c-2.86-1.21-6.08-1.21-8.94 0L5.65 5.67c-.19-.29-.58-.38-.87-.2-.28.18-.37.54-.22.83L6.4 9.48C3.3 11.25 1.28 14.44 1 18h22c-.28-3.56-2.3-6.75-5.4-8.52zM7 15.25c-.69 0-1.25-.56-1.25-1.25s.56-1.25 1.25-1.25 1.25.56 1.25 1.25-.56 1.25-1.25 1.25zm10 0c-.69 0-1.25-.56-1.25-1.25s.56-1.25 1.25-1.25 1.25.56 1.25 1.25-.56 1.25-1.25 1.25z" />
        </svg>
      )
    }
    if (osLower.includes('mac')) {
      return (
        <svg className="h-5 w-5 text-gray-700" viewBox="0 0 24 24" fill="currentColor">
          <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
        </svg>
      )
    }
    // Default device icon
    return (
      <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
      </svg>
    )
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Never'
    return new Date(dateString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  // Map Windows build numbers to friendly names
  const getWindowsVersionName = (version?: string): string => {
    if (!version) return ''

    // Windows 11 builds start at 22000
    // Windows 10 builds: 10240 (1507), 10586 (1511), 14393 (1607), 15063 (1703),
    // 16299 (1709), 17134 (1803), 17763 (1809), 18362 (1903), 18363 (1909),
    // 19041 (2004), 19042 (20H2), 19043 (21H1), 19044 (21H2), 19045 (22H2)
    const buildMatch = version.match(/10\.0\.(\d+)/)
    if (buildMatch) {
      const build = parseInt(buildMatch[1])
      if (build >= 22631) return 'Windows 11 23H2'
      if (build >= 22621) return 'Windows 11 22H2'
      if (build >= 22000) return 'Windows 11 21H2'
      if (build >= 19045) return 'Windows 10 22H2'
      if (build >= 19044) return 'Windows 10 21H2'
      if (build >= 19043) return 'Windows 10 21H1'
      if (build >= 19042) return 'Windows 10 20H2'
      if (build >= 19041) return 'Windows 10 2004'
      if (build >= 18363) return 'Windows 10 1909'
      if (build >= 18362) return 'Windows 10 1903'
      if (build >= 17763) return 'Windows 10 1809'
      if (build >= 17134) return 'Windows 10 1803'
      if (build >= 16299) return 'Windows 10 1709'
      if (build >= 15063) return 'Windows 10 1703'
      if (build >= 14393) return 'Windows 10 1607'
      if (build >= 10586) return 'Windows 10 1511'
      if (build >= 10240) return 'Windows 10 1507'
    }
    return ''
  }

  const getOSDisplayName = (os?: string, version?: string): { name: string; detail: string } => {
    const osLower = (os || '').toLowerCase()

    if (osLower.includes('windows')) {
      const friendlyName = getWindowsVersionName(version)
      return {
        name: friendlyName || os || 'Windows',
        detail: friendlyName ? `Build ${version}` : (version || ''),
      }
    }
    if (osLower.includes('ios')) {
      return { name: `iOS ${version || ''}`.trim(), detail: '' }
    }
    if (osLower.includes('android')) {
      return { name: `Android ${version || ''}`.trim(), detail: '' }
    }
    if (osLower.includes('macos') || osLower.includes('mac os')) {
      return { name: `macOS ${version || ''}`.trim(), detail: '' }
    }

    return { name: os || 'Unknown', detail: version || '' }
  }

  // Status badge configurations with tooltips
  const getComplianceBadge = (isCompliant?: boolean) => {
    if (isCompliant === undefined) return null

    const config = isCompliant
      ? {
          color: 'bg-green-100 text-green-800',
          label: 'Compliant',
          tooltip: 'Device meets all organization compliance policies',
        }
      : {
          color: 'bg-red-100 text-red-800',
          label: 'Not Compliant',
          tooltip: 'Device does not meet one or more compliance policies - action may be required',
        }

    return (
      <span
        className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${config.color} cursor-help`}
        title={config.tooltip}
      >
        {config.label}
      </span>
    )
  }

  const getManagedBadge = (isManaged?: boolean) => {
    if (isManaged === undefined) return null

    const config = isManaged
      ? {
          color: 'bg-blue-100 text-blue-800',
          label: 'Managed',
          tooltip: 'Device is enrolled in Intune or another MDM solution',
        }
      : {
          color: 'bg-gray-100 text-gray-800',
          label: 'Unmanaged',
          tooltip: 'Device is not enrolled in any management solution',
        }

    return (
      <span
        className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${config.color} cursor-help`}
        title={config.tooltip}
      >
        {config.label}
      </span>
    )
  }

  const getTrustTypeBadge = (trustType?: string) => {
    if (!trustType) return null

    const trustConfig: Record<string, { color: string; label: string; tooltip: string }> = {
      'AzureAd': {
        color: 'bg-purple-100 text-purple-800',
        label: 'Azure AD Joined',
        tooltip: 'Device is joined directly to Azure AD (cloud-only)',
      },
      'ServerAd': {
        color: 'bg-indigo-100 text-indigo-800',
        label: 'Domain Joined',
        tooltip: 'Device is joined to on-premises Active Directory',
      },
      'Workplace': {
        color: 'bg-cyan-100 text-cyan-800',
        label: 'Workplace Joined',
        tooltip: 'Personal device registered for work access (BYOD)',
      },
    }

    const config = trustConfig[trustType] || {
      color: 'bg-gray-100 text-gray-800',
      label: trustType,
      tooltip: 'Device trust type',
    }

    return (
      <span
        className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${config.color} cursor-help`}
        title={config.tooltip}
      >
        {config.label}
      </span>
    )
  }

  const renderDeviceTable = (devices: M365Device[], title: string, isOwned: boolean) => (
    <div className="bg-white shadow sm:rounded-lg">
      <div className="px-4 py-5 sm:p-6">
        <h3 className="text-lg font-medium leading-6 text-gray-900 mb-4">
          {title} ({devices.length})
        </h3>
        {devices.length === 0 ? (
          <p className="text-sm text-gray-500 italic">No {isOwned ? 'owned' : 'registered'} devices</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Device
                  </th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    OS
                  </th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Last Sign-in
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {devices.map(device => (
                  <tr key={device.id} className="hover:bg-gray-50">
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0">
                          {getOSIcon(device.operatingSystem)}
                        </div>
                        <div className="ml-3">
                          <div className="text-sm font-medium text-gray-900">{device.displayName}</div>
                          {(device.manufacturer || device.model) && (
                            <div className="text-xs text-gray-500">
                              {[device.manufacturer, device.model].filter(Boolean).join(' ')}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      {(() => {
                        const osDisplay = getOSDisplayName(device.operatingSystem, device.operatingSystemVersion)
                        return (
                          <>
                            <div className="text-sm text-gray-900">{osDisplay.name}</div>
                            {osDisplay.detail && (
                              <div className="text-xs text-gray-500">{osDisplay.detail}</div>
                            )}
                          </>
                        )
                      })()}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex flex-col gap-1">
                        {getComplianceBadge(device.isCompliant)}
                        {getManagedBadge(device.isManaged)}
                        {getTrustTypeBadge(device.trustType)}
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(device.approximateLastSignInDateTime)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )

  if (loading) {
    return (
      <div className="bg-white shadow sm:rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex items-center justify-center py-8">
            <svg className="animate-spin h-6 w-6 text-primary-600" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
            </svg>
            <span className="ml-2 text-sm text-gray-500">Loading devices...</span>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-white shadow sm:rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex">
              <svg className="h-5 w-5 text-yellow-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <div>
                <h3 className="text-sm font-medium text-yellow-800">Unable to load devices</h3>
                <p className="mt-1 text-sm text-yellow-700">{error}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {renderDeviceTable(ownedDevices, 'Owned Devices', true)}
      {renderDeviceTable(registeredDevices, 'Registered Devices', false)}
    </div>
  )
}
